/**
 * Drops an admin-managed content area into an existing hard-coded route.
 *
 *   <CmsRegion id="home-below-hero" label="Home — below hero" />
 *
 * Public visitors see the published blocks (nothing at all when empty).
 * In edit mode the area always renders — even when empty — so it shows up in
 * the Store Editor outline and can be filled in.
 */
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { BlockCanvas } from "@/components/cms/BlockCanvas";
import { useCmsEdit } from "@/components/cms/cms-edit";
import { getPublishedCmsPage, adminGetCmsPage } from "@/lib/cms.functions";
import type { Block } from "@/lib/cms/blocks";

export function CmsRegion({ id, label }: { id: string; label?: string }) {
  const path = `region:${id}`;
  const cms = useCmsEdit();
  const getPublished = useServerFn(getPublishedCmsPage);
  const getDraft = useServerFn(adminGetCmsPage);

  // Drafts arrive with the preview session when we are inside the Store
  // Editor; only a same-host admin needs to fetch them.
  const sessionDoc = cms.sessionDrafts?.[path];
  const needsFetch = cms.active && !cms.sessionDrafts;

  const published = useQuery({
    queryKey: ["cms", "region", path],
    queryFn: () => getPublished({ data: { path } }),
    staleTime: 60_000,
    enabled: !cms.active,
  });

  const draft = useQuery({
    queryKey: ["cms", "region", "draft", path],
    queryFn: () => getDraft({ data: { path } }),
    enabled: needsFetch,
    retry: false,
  });

  if (cms.active) {
    const blocks: Block[] = sessionDoc?.blocks ?? draft.data?.draft.blocks ?? [];
    // Render immediately (even while a same-host fetch is in flight) so the
    // area registers itself with the editor rather than appearing to hang.
    return <BlockCanvas path={path} label={label ?? id} blocks={blocks} />;
  }

  const blocks: Block[] = published.data?.doc.blocks ?? [];
  if (!blocks.length) return null;
  return <BlockCanvas path={path} label={label ?? id} blocks={blocks} />;
}

export default CmsRegion;
